import { keywordKey, type TemplateKeyword, type TemplateKeywordCategory } from "@adpilot/shared";
import {
  IconArrowDown,
  IconArrowsSort,
  IconChevronDown,
  IconDotsVertical,
  IconTrash,
} from "@tabler/icons-react";
import { forwardRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { DataTablePagination } from "@/components/patterns/DataTablePagination";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Menu,
  MenuGroup,
  MenuGroupLabel,
  MenuItem,
  MenuPopup,
  MenuSeparator,
  MenuSub,
  MenuSubPopup,
  MenuSubTrigger,
  MenuTrigger,
} from "@/components/ui/menu";
import { cn } from "@/lib/utils";
import { KEYWORD_POOLS, type KeywordPool } from "./model";

interface KeywordTableProps {
  pool: KeywordPool;
  /** Rows shown (search applied), already sorted. */
  rows: TemplateKeyword[];
  onSelect: (keys: string[], selected: boolean) => void;
  onMove: (keys: string[], category: TemplateKeywordCategory) => void;
}

const checkboxClassName =
  "size-4 rounded-[4px] border-control-border-hover bg-surface data-checked:border-ink data-checked:bg-ink data-checked:text-surface data-indeterminate:border-ink data-indeterminate:bg-ink data-indeterminate:text-surface";

const numberCell =
  "shrink-0 text-right font-mono text-xs tabular-nums leading-4 text-ink data-[empty]:text-muted-foreground";

const PAGE_SIZE = 50;

const integer = (value: number | null) => (value === null ? "—" : value.toLocaleString("en-US"));
const decimal = (value: number | null) =>
  value === null ? "—" : value.toLocaleString("en-US", { maximumFractionDigits: 1 });
const money = (value: number | null) => (value === null ? "—" : `$${value.toFixed(2)}`);

export function KeywordTable({ pool, rows, onSelect, onMove }: KeywordTableProps) {
  const { t } = useTranslation("templatePortfolio");
  const selectedKeys = rows.filter((row) => row.selected).map((row) => keywordKey(row.text));
  const allKeys = rows.map((row) => keywordKey(row.text));
  const all = rows.length > 0 && selectedKeys.length === rows.length;
  const some = selectedKeys.length > 0 && !all;
  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const [requestedPage, setPage] = useState(1);
  // Edits can shrink the pool (e.g. moving keywords out), so clamp instead of resetting.
  const page = Math.min(requestedPage, pageCount);
  const pageRows = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="flex flex-col overflow-clip rounded-[14px] border border-line bg-surface">
      {selectedKeys.length > 0 ? (
        <div className="flex h-11 shrink-0 items-center gap-3 border-b border-badge-information-border bg-badge-information-background px-3">
          <span className="text-xs font-semibold text-badge-information-foreground">
            {t("keywords.bulk.selected", { count: selectedKeys.length })}
          </span>
          <span aria-hidden className="h-4 w-px shrink-0 bg-badge-information-border" />
          <Menu>
            <MenuTrigger render={<BulkButton />}>
              <IconArrowsSort className="size-3.5" aria-hidden />
              {t("keywords.bulk.changeCategory")}
              <IconChevronDown className="size-3.5" aria-hidden />
            </MenuTrigger>
            <MenuPopup align="start">
              <CategoryItems current={pool} onPick={(category) => onMove(selectedKeys, category)} />
            </MenuPopup>
          </Menu>
          {pool === "discard" ? null : (
            <BulkButton onClick={() => onMove(selectedKeys, "discard")}>
              <IconTrash className="size-3.5" aria-hidden />
              {t("keywords.bulk.discard")}
            </BulkButton>
          )}
          <button
            type="button"
            className="ml-auto cursor-pointer text-xs text-badge-information-foreground hover:underline"
            onClick={() => onSelect(selectedKeys, false)}
          >
            {t("keywords.bulk.clear")}
          </button>
        </div>
      ) : null}
      <div className="flex h-9 shrink-0 items-center gap-3 border-b border-line bg-table-footer px-3 text-xs font-medium text-muted-foreground">
        <Checkbox
          className={checkboxClassName}
          checked={all}
          indeterminate={some}
          aria-label={t("keywords.table.selectAll")}
          onCheckedChange={(checked) => onSelect(allKeys, checked)}
        />
        <span className="w-[200px] shrink-0">{t("keywords.table.keyword")}</span>
        <span className="w-[104px] shrink-0">{t("keywords.table.source")}</span>
        <span className="min-w-0 flex-1">{t("keywords.table.reason")}</span>
        <span className="flex w-[84px] shrink-0 items-center justify-end gap-0.5 font-semibold text-ink">
          {t("keywords.table.monthlyPurchases")}
          <IconArrowDown className="size-3" aria-hidden />
        </span>
        <span className="w-[84px] shrink-0 text-right">
          {t("keywords.table.monthlySearchVolume")}
        </span>
        <span className="w-14 shrink-0 text-right">{t("keywords.table.relevance")}</span>
        <span className="w-[72px] shrink-0 text-right">{t("keywords.table.ppcBid")}</span>
        <span className="w-5 shrink-0" />
      </div>
      {pageRows.map((row) => {
        const key = keywordKey(row.text);
        return (
          <div
            key={key}
            className="flex h-12 shrink-0 items-center gap-3 border-b border-line px-3 last:border-b-0 [content-visibility:auto] [contain-intrinsic-size:auto_48px]"
          >
            <Checkbox
              className={checkboxClassName}
              checked={row.selected}
              aria-label={t("keywords.table.select", { keyword: row.text })}
              onCheckedChange={(checked) => onSelect([key], checked)}
            />
            <span className="w-[200px] shrink-0 truncate text-[13px] font-medium leading-[18px] text-ink">
              {row.text}
            </span>
            <span className="flex w-[104px] shrink-0">
              <SourceBadge source={row.source} />
            </span>
            <span
              className={cn(
                "min-w-0 flex-1 truncate text-xs",
                row.reason ? "text-text-secondary" : "text-muted-foreground",
              )}
              title={row.reason ?? undefined}
            >
              {row.reason ?? "—"}
            </span>
            <Metric className="w-[84px]" value={integer(row.metrics.monthlyPurchases)} />
            <Metric className="w-[84px]" value={integer(row.metrics.monthlySearchVolume)} />
            <Metric className="w-14" value={decimal(row.metrics.relevance)} />
            <Metric className="w-[72px]" value={money(row.metrics.ppcBid)} />
            <Menu>
              <MenuTrigger
                render={
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    className="size-5 shrink-0 text-muted-foreground"
                    aria-label={t("keywords.table.rowMenu")}
                  />
                }
              >
                <IconDotsVertical className="size-4" aria-hidden />
              </MenuTrigger>
              <MenuPopup align="end">
                <MenuSub>
                  <MenuSubTrigger>{t("keywords.rowMenu.moveTo")}</MenuSubTrigger>
                  <MenuSubPopup>
                    <CategoryItems
                      current={pool}
                      onPick={(category) => onMove([key], category)}
                      label={false}
                    />
                  </MenuSubPopup>
                </MenuSub>
                {pool === "discard" ? null : (
                  <>
                    <MenuSeparator />
                    <MenuItem onClick={() => onMove([key], "discard")}>
                      <IconTrash aria-hidden />
                      {t("keywords.bulk.discard")}
                    </MenuItem>
                  </>
                )}
              </MenuPopup>
            </Menu>
          </div>
        );
      })}
      {rows.length > PAGE_SIZE ? (
        <DataTablePagination
          className="border-t border-line px-3 py-2.5"
          page={page}
          pageCount={pageCount}
          summary={t("keywords.pagination", {
            start: (page - 1) * PAGE_SIZE + 1,
            end: Math.min(page * PAGE_SIZE, rows.length),
            total: rows.length,
          })}
          onPageChange={setPage}
        />
      ) : null}
    </div>
  );
}

function Metric({ value, className }: { value: string; className: string }) {
  return (
    <span className={cn(numberCell, className)} data-empty={value === "—" ? "" : undefined}>
      {value}
    </span>
  );
}

const BulkButton = forwardRef<HTMLButtonElement, React.ComponentProps<"button">>(
  function BulkButton(props, ref) {
    return (
      <button
        ref={ref}
        type="button"
        {...props}
        className="flex h-7 cursor-pointer items-center gap-1.5 rounded-md border border-line bg-surface px-2.5 text-xs font-medium text-ink outline-none hover:bg-subtle focus-visible:ring-2 focus-visible:ring-ring/15"
      />
    );
  },
);

function CategoryItems({
  current,
  onPick,
  label = true,
}: {
  current: KeywordPool;
  onPick: (category: TemplateKeywordCategory) => void;
  label?: boolean;
}) {
  const { t } = useTranslation("templatePortfolio");
  return (
    <MenuGroup>
      {label ? <MenuGroupLabel>{t("keywords.rowMenu.moveTo")}</MenuGroupLabel> : null}
      {KEYWORD_POOLS.filter((pool) => pool !== current).map((pool) => (
        <MenuItem key={pool} onClick={() => onPick(pool)}>
          {t(`keywords.pools.${pool}`)}
        </MenuItem>
      ))}
    </MenuGroup>
  );
}

const SOURCE_BADGE = {
  ai_generated:
    "border-badge-information-border bg-badge-information-background text-badge-information-foreground",
  default: "border-badge-neutral-border bg-badge-neutral-background text-badge-neutral-foreground",
} as const;

export function SourceBadge({ source }: { source: TemplateKeyword["source"] }) {
  const { t } = useTranslation("templatePortfolio");
  return (
    <span
      className={cn(
        "flex h-5 items-center whitespace-nowrap rounded-md border px-1.5 text-[11px] font-medium leading-[14px]",
        source === "ai_generated" ? SOURCE_BADGE.ai_generated : SOURCE_BADGE.default,
      )}
    >
      {t(`keywords.source.${source}`)}
    </span>
  );
}

/** TP-04a: placeholder rows while the first classification runs. */
export function KeywordTableSkeleton() {
  return (
    <div className="flex flex-col overflow-clip rounded-[14px] border border-line bg-surface">
      <div className="h-9 shrink-0 border-b border-line bg-table-footer" />
      {[160, 120, 180].map((width) => (
        <div
          key={width}
          className="flex h-12 shrink-0 items-center gap-3 border-b border-line px-3 last:border-b-0"
        >
          <span className="size-4 shrink-0 rounded-[4px] bg-subtle" />
          <span className="h-2.5 shrink-0 rounded-[5px] bg-subtle" style={{ width }} />
          <span className="h-4 w-20 shrink-0 rounded-md bg-subtle" />
          <span className="h-2.5 flex-1 rounded-[5px] bg-subtle" />
          <span className="h-2.5 w-60 shrink-0 rounded-[5px] bg-subtle" />
        </div>
      ))}
    </div>
  );
}
